import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { FieldEditForm } from "Components/UserAccount/Shared/PublicInformation/FieldEditForm";
import { usePartyFieldSave } from "Components/UserAccount/Shared/PublicInformation/usePartyFieldSave";
import type { PartyDto } from "Infrastructure/Api/Api";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { z } from "zod";

const createEmailSchema = (t: TFunction) =>
  z.object({
    email: z.string().email(t(Resources.Validation.InvalidEmail)).nullable(),
  });

type EmailFormValues = z.infer<ReturnType<typeof createEmailSchema>>;

type Props = {
  party: PartyDto;
  onSuccess: () => void;
};

/** Email slice of the party contact edit (e-mail card's mini dialog). */
export const EmailForm = ({ party, onSuccess }: Props) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(createEmailSchema(t)),
    defaultValues: { email: party.email ?? null },
  });

  const { saveParty, isSaving } = usePartyFieldSave(party, onSuccess);

  if (!party.publicID) {
    return <ErrorText>{t(Resources.Errors.MissingId)}</ErrorText>;
  }

  return (
    <FieldEditForm
      onSubmit={handleSubmit((data) => saveParty({ email: data.email }))}
      isSubmitDisabled={!isDirty}
      isSaving={isSaving}
    >
      <FormInput
        control={control}
        errors={errors}
        name="email"
        label={t(Resources.Common.Email)}
        type="email"
        autoFocus
        valueTransformer={(v) => v.toLowerCase()}
      />
    </FieldEditForm>
  );
};
